import React, { useState } from 'react';
import styles from '~/src/components/Modal/styles';

import Button from '~/src/components/Button';
import { SpinnerWithTitle } from '~/src/components/Spinner';
import { ReactComponent as IconError } from '~/src/static/icon-error.svg';
import { ModalContent } from '~/src/components/Modal/Generic';
import analyticsService from '~/src/services/analytics';
import { useUpdateUserPreferences } from '~/src/entities/user';
import { APIError } from '~/src/utils/error';

export type ModalVariant = 'default' | 'populate' | 'coreField';

export type SyncSingleMatterModalContentProps = {
  onConfirm: () => void;
  isError: boolean;
  onCancel: () => void;
  requiresConfirm: boolean;
  error: APIError | null;
  variant?: ModalVariant;
  hideCheckbox?: boolean;
};

const VARIANT_CONTENT: Record<
  ModalVariant,
  {
    title: string;
    bodyContent: React.ReactNode;
    cancelButtonText: string;
    confirmButtonText: string;
    checkboxLabel: string;
    loadingTitle: string;
    loadingSubtitle: string;
  }
> = {
  default: {
    title: 'Overwrite Clio Draft fields with Clio Manage content?',
    bodyContent: (
      <>
        All Clio Draft fields that have a different content in Clio Manage will
        be overwritten.
        <br />
        <br />
        <u>
          <a
            href="https://help.clio.com/hc/en-us/articles/24315032920987-Clio-Draft-Matters#h_01HT5J2ER26R8XYHHABVR17RXW"
            target={'_blank'}
            rel="noreferrer"
          >
            Learn More
          </a>
        </u>
      </>
    ),
    cancelButtonText: 'Close',
    confirmButtonText: 'Get Updates from Clio Manage',
    checkboxLabel: "Don't show this again",
    loadingTitle: 'Retrieving updates...',
    loadingSubtitle: 'Please Wait',
  },
  populate: {
    title: 'Refresh Clio Manage data',
    bodyContent: (
      <>
        Refresh from Clio Manage to auto-fill document fields.
        <br />
        <br />
        Document fields linked to Clio Manage data will be replaced with the
        latest information.
      </>
    ),
    cancelButtonText: 'Cancel',
    confirmButtonText: 'Refresh Clio Manage data',
    checkboxLabel: 'Do not show this message again',
    loadingTitle: 'Refreshing your Clio Manage data (1 of 2)...',
    loadingSubtitle: '',
  },
  coreField: {
    title: 'Use Clio Manage data',
    bodyContent: (
      <>
        Refresh from Clio Manage to auto-fill document fields.
        <br />
        <br />
        Document fields linked to Clio Manage data will be replaced with the
        latest information.
      </>
    ),
    cancelButtonText: 'Cancel',
    confirmButtonText: 'Refresh Clio Manage data',
    checkboxLabel: '',
    loadingTitle: 'Retrieving data from Clio Manage...',
    loadingSubtitle: 'Please Wait',
  },
};

const SyncSingleMatterModalContent = ({
  onConfirm,
  isError,
  requiresConfirm,
  onCancel,
  error,
  variant = 'default',
  hideCheckbox = false,
}: SyncSingleMatterModalContentProps) => {
  const { mutate } = useUpdateUserPreferences({
    onMutate: () => {
      onConfirm();
      analyticsService.track(`Single Matter Sync - Do not prompt again`);
    },
  });
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (requiresConfirm) {
    const {
      title,
      bodyContent,
      cancelButtonText,
      confirmButtonText,
      checkboxLabel,
    } = VARIANT_CONTENT[variant];

    return (
      <ModalContent
        title={title}
        size={'small'}
        actions={
          <>
            <Button
              cssStyle={styles.buttonAction}
              onClick={() => {
                analyticsService.track(
                  'Single Matter Sync - Canceled At Overwrite Prompt',
                );
                onCancel();
              }}
            >
              {cancelButtonText}
            </Button>

            <Button
              cssStyle={styles.buttonAction}
              primary
              analyticsname="Single Matter Sync - Get Updates from Clio Manage"
              onClick={() => {
                if (dontShowAgain) {
                  mutate({ warn_sync_overwrite: false });
                } else {
                  onConfirm();
                }
              }}
            >
              {confirmButtonText}
            </Button>
          </>
        }
      >
        <>
          <div style={{ marginBottom: hideCheckbox ? 0 : 24 }}>
            {bodyContent}
          </div>
          {!hideCheckbox && (
            <div>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  style={{ marginRight: '9px' }}
                  checked={dontShowAgain}
                  onChange={() => setDontShowAgain(!dontShowAgain)}
                  analyticsname="Single Matter Sync - Don't show this again"
                />
                {checkboxLabel}
              </label>
            </div>
          )}
        </>
      </ModalContent>
    );
  }

  if (isError) {
    let title: string;
    let message: string;
    let titleIcon: React.ReactNode | null;
    switch (error?.response?.status) {
      case 423:
        title = 'Another sync in progress';
        titleIcon = null;
        message =
          'Your Clio matter sync could not be started because another sync is currently in progress on your account. Please try again in a few minutes.';
        break;
      default:
        title = 'Unable to retrieve updates';
        titleIcon = <IconError style={{ marginRight: '16px' }} />;
        message =
          'We were unable to retrieve the latest updates from Clio Manage. Please check your Clio matter permissions and try again.';
        break;
    }
    return (
      <ModalContent
        title={title}
        size={'small'}
        actions={
          <Button primary cssStyle={styles.buttonAction} onClick={onCancel}>
            Close
          </Button>
        }
        titleIcon={titleIcon}
      >
        <>
          <div style={{ marginBottom: 24 }}>{message}</div>
          <div>If the problem persists, reach out to customer support.</div>
        </>
      </ModalContent>
    );
  }

  const { loadingTitle, loadingSubtitle } = VARIANT_CONTENT[variant];

  return (
    <ModalContent size={'small'}>
      <SpinnerWithTitle title={loadingTitle} subtitle={loadingSubtitle} />
    </ModalContent>
  );
};

export default SyncSingleMatterModalContent;
